// pages/AssignedOrders.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useSeller } from '../contexts/SellerContext';
import Header from '../components/Header';
import './Pickup.css';
import { useOrderOperations } from '../hooks/useOrderOperations';
import { useJREFetcher } from '../hooks/useJREFetcher';
import { OrderGroupList } from '../components/OrderGroupList';
import { LoadingState, ErrorState, EmptyState } from '../components/UIStates';

// Constants
export const OTP_LENGTH = 6;
export const OTP_EXPIRY_MINUTES = 10;
export const USE_CASE_SECURE_DISPATCH = 'SECURE_DISPATCH';

const Payment = () => {
  const { seller } = useSeller();
  const sellerId = seller?.sellerId;

  // State
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedGroup, setExpandedGroup] = useState(null);

  // Secure Dispatch state
  const [, setSecureDispatchModal] = useState(null);

  // Custom hooks
  const { jreMap, operatorMap, courierMap, fetchJREData, fetchOperatorData, fetchCourierData } = useJREFetcher();
  const { calculateOrderMetrics, calculateDispatchSummary } = useOrderOperations();

  // Fetch assigned orders
  useEffect(() => {
    const fetchAssignedOrders = async () => {
      if (!sellerId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const q = query(
          collection(db, 'orderList'),
          where('sellerId', '==', sellerId),
          where('orderStatus', 'in', ['buyerPaid', 'paymentDispatchedtoOperator','paymentDispatchedtoSeller','paymentDeliveredToSeller'])
        );

        const snapshot = await getDocs(q);
        const fetchedOrders = snapshot.docs.map(docSnap => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setOrders(fetchedOrders);
      } catch (err) {
        console.error('Error fetching orders:', err);
        setError('Failed to load orders');
      } finally {
        setLoading(false);
      }
    };

    fetchAssignedOrders();
  }, [sellerId]);

  // Preload JREs, Operators and Courier branches when orders load
  useEffect(() => {
    if (orders.length > 0) {
      const jreIds = [...new Set(
        orders
          .map(o => o.jreId)
          .filter(id => id && id !== 'No JRE')
      )];
      const operatorIds = [...new Set(
        orders
          .map(o => o.operatorId)
          .filter(id => id && id !== 'Unknown Operator')
      )];
      const courierIds = [...new Set(
        orders
          .map(o => o.courierId)
          .filter(id => id)
      )];
      jreIds.forEach(fetchJREData);
      operatorIds.forEach(fetchOperatorData);
      courierIds.forEach(fetchCourierData);
    }
  }, [orders, fetchJREData, fetchOperatorData, fetchCourierData]);

  // Group orders with JRE and Operator numbers
  const ordersByOperatorAndJRE = useMemo(() => {
    const grouped = {};

    orders.forEach((order) => {
      const operatorId = order.operatorId || 'Unknown Operator';
      const jreId = order.jreId || 'No JRE';
      const courierId = order.courierId || null;
      const isCourier = order.dispatchType === 'courier' || !!courierId;
      const groupKey = `${operatorId}_${jreId}_${isCourier ? `courier_${courierId}` : 'pickup'}`;

      const jreInfo = jreMap[jreId] || {};
      const operatorInfo = operatorMap[operatorId] || {};
      const courierInfo = (courierId && courierMap[courierId]) || {};
      const courierName = order.courierName || courierInfo.courierName || null;
      const courierBranchName = order.courierBranchName || courierInfo.branchName || null;
      const courierContactPerson = order.courierContactPerson || courierInfo.contactPerson || null;
      const courierContactNumber = order.courierContactNumber || courierInfo.contactNumber || null;

      const jrePrimaryMobile = order.jreMobile || order.jreContact || order.jrePrimaryMobile || jreInfo.primaryMobile || null;
      const operatorMobile = order.operatorMobile || order.operatorContact || operatorInfo.contact || jreInfo.operatorNumber || null;

      if (!grouped[groupKey]) {
        grouped[groupKey] = {
          groupKey,
          operatorId,
          jreId,
          operatorName: order.operatorName || order.operatorOrganizationName || operatorInfo.name || operatorId,
          jreName: order.jreName || jreInfo.name || jreId,
          courierId,
          courierName,
          courierBranchName,
          courierContactPerson,
          courierContactNumber,
          isCourier,
          jrePrimaryMobile,
          operatorMobile,
          jreOperatorNumber: operatorMobile, // backward compatibility
          orders: [],
          totalItems: 0,
          totalFineWeight: 0,
          totalMc: 0,
        };
      }

      const metrics = calculateOrderMetrics(order);
      grouped[groupKey].orders.push({ ...order, ...metrics });
      grouped[groupKey].totalItems += getQuantity(order);
      grouped[groupKey].totalFineWeight += metrics.fineWeight;
      grouped[groupKey].totalMc += metrics.totalMc;
    });

    return Object.values(grouped);
  }, [orders, calculateOrderMetrics, jreMap, operatorMap, courierMap]);

  // Toggle accordion
  const toggleGroup = useCallback((groupKey) => {
    setExpandedGroup(current => current === groupKey ? null : groupKey);
  }, []);

  if (loading) return <LoadingState title="Payment Orders" />;
  if (error) return <ErrorState title="Payment Orders" error={error} />;

  return (
    <>
      <Header title="Payment Orders" />
      <div className="orders-container">
        <div className="orders-header">
          <p>{orders.length} payment order{orders.length !== 1 ? 's' : ''} found</p>
        </div>

        {orders.length === 0 ? (
          <EmptyState message="No payment orders found" />
        ) : (
          <OrderGroupList
            groups={ordersByOperatorAndJRE}
            expandedGroup={expandedGroup}
            onToggleGroup={toggleGroup}
            onSecureDispatch={setSecureDispatchModal}
            calculateDispatchSummary={calculateDispatchSummary}
          />
        )}
      </div>

    
    </>
  );
};

// Utility functions
const getQuantity = (order) => order.orderPiece ? parseInt(order.orderPiece) : 1;

export default React.memo(Payment);